// src/v2/components/ConsolidatedFilter.jsx
//
// Config-driven multi-select filter component used by OfficesPage.
//
// Props:
//   dimensions         — [{ id: string, label: string, options: [{ value, label }] }]
//   selections         — { [dimensionId]: string[] }  (controlled)
//   onChange           — (newSelections: { [dimensionId]: string[] }) => void
//   onClear            — optional () => void called after clearAll (no notification)
//   testId             — optional data-testid for the trigger button
//   baselineSelections — optional { [dimensionId]: string[] } — the "default" state.
//                        Badge counts groups that deviate from this baseline.
//                        "Reset to default" restores to these values.
//   groupDefaultStates — optional default-state overrides for URL intent
//   onResetDefaults    — optional (groupId?) => void; no ID resets all groups

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { T } from "../../app/tokens.js";
import { activeFilterGroups } from "../../utils/filterHiddenSearch.js";
import { normalizeCheckedSelections, allOptionValues, selectionSetsEqual } from "../../utils/checkedSelections.js";

export function ConsolidatedFilter({ dimensions = [], selections = {}, onChange, onClear, testId, baselineSelections = {}, checkedInclusion = false, unifiedDefaults = false, viewOptions, groupDefaultStates = {}, onResetDefaults }) {
  if (checkedInclusion) selections = normalizeCheckedSelections(dimensions, selections, baselineSelections);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);

  // Count groups that deviate from their baseline (not individual options).
  // URL intent can differ from unrestricted defaults even when the visible
  // checked arrays coincide (legacy all-known restrictions or empty groups).
  const activeGroups = activeFilterGroups(dimensions, selections, baselineSelections, checkedInclusion);
  const totalGroupsActive = dimensions.filter(dim => groupDefaultStates[dim.id] === undefined
    ? activeGroups.includes(dim) : !groupDefaultStates[dim.id]).length;
  const showBadge = totalGroupsActive > 0;

  // Compute and keep panel position anchored to the trigger button via fixed coords.
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;

    function updatePos() {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPos({ top: rect.bottom + 6, left: rect.left });
    }

    updatePos();

    window.addEventListener("scroll", updatePos, { passive: true, capture: true });
    window.addEventListener("resize", updatePos, { passive: true });
    return () => {
      window.removeEventListener("scroll", updatePos, { capture: true });
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  // Outside-click and Escape handlers — extended to include portaled panelRef.
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        !(panelRef.current && panelRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function toggleOption(dimensionId, value) {
    const current = selections[dimensionId] ?? (baselineSelections[dimensionId] ?? []);
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...selections, [dimensionId]: next });
  }

  function toggleGroupAll(dimensionId) {
    const dim = dimensions.find((d) => d.id === dimensionId);
    if (!dim) return;
    if (dim.defaultValues) {
      onChange({ ...selections, [dimensionId]: [...dim.defaultValues] });
      return;
    }
    const current = selections[dimensionId] ?? (baselineSelections[dimensionId] ?? []);
    const allValues = dim.options.map((o) => o.value);
    const allChecked = allValues.every((v) => current.includes(v));
    onChange({ ...selections, [dimensionId]: checkedInclusion ? allValues : allChecked ? [] : allValues });
  }

  function clearAll() {
    if (onResetDefaults) onResetDefaults();
    else onChange(checkedInclusion ? normalizeCheckedSelections(dimensions, {}, baselineSelections) : baselineSelections);
    onClear?.();
  }

  const panel = open && panelPos && ReactDOM.createPortal(
    <div
      ref={panelRef}
      data-testid="consolidated-filter-panel"
      style={{
        position: "fixed",
        top: panelPos.top,
        left: panelPos.left,
        zIndex: 80,
        background: "var(--v2-card)",
        border: "1px solid var(--v2-line)",
        borderRadius: 14,
        boxShadow: "var(--v2-shadow-drop)",
        padding: "0 0 0",
        width: 280,
        maxHeight: 480,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Help text */}
      <div
        data-testid="filter-help-text"
        style={{
          padding: "10px 16px 9px",
          fontSize: T.fsSm,
          color: "var(--v2-light)",
          borderBottom: "1px solid var(--v2-hair)",
          lineHeight: 1.4,
          flexShrink: 0,
        }}
      >
        {unifiedDefaults ? "Ticked values are shown. Within a group any match; across groups all must match." : checkedInclusion
          ? "Checked boxes include values. All boxes checked = everything; uncheck to narrow. No boxes checked = no matches. Within a group, any checked value matches (OR); across groups, all must match (AND). Reset restores this page’s defaults."
          : <>Filters narrow the list. Within a group, any match shows (OR). Across groups, all must match (AND). An untouched group means &ldquo;all.&rdquo;</>}
      </div>

      {/* Optional view settings are not filters: no badge/reset/selection coupling. */}
      {viewOptions}
      {/* Dimension groups */}
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
        {dimensions.map((dim, dimIdx) => (
          <div key={dim.id}>
            {dimIdx > 0 && (
              <div style={{ height: 1, background: "var(--v2-hair)", margin: "10px 0" }} />
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 16px 6px",
              }}
            >
              <span
                style={{
                  fontSize: T.fsXs,
                  fontWeight: T.fwBold,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  color: "var(--v2-light)",
                }}
              >
                {dim.label}
              </span>
              {unifiedDefaults ? (() => {
                const current = selections[dim.id] ?? [];
                const baseline = baselineSelections[dim.id] ?? (dim.restriction ? [] : allOptionValues(dim));
                const atDefault = groupDefaultStates[dim.id] ?? selectionSetsEqual(current, baseline);
                const allChecked = dim.options.length === 0 && groupDefaultStates[dim.id] !== undefined
                  ? groupDefaultStates[dim.id] : selectionSetsEqual(current, allOptionValues(dim));
                return <span style={{ display: "inline-flex", gap: 12 }}>
                  <button type="button" data-testid={`filter-group-default-${dim.id}`}
                    disabled={atDefault}
                    onClick={() => onResetDefaults ? onResetDefaults(dim.id) : onChange({ ...selections, [dim.id]: [...baseline] })}
                    style={{ background: "none", border: 0, padding: 0, fontSize: T.fsSm, fontWeight: T.fwSemibold,
                      color: atDefault ? "var(--v2-light)" : "var(--v2-accent)", cursor: atDefault ? "default" : "pointer" }}>
                    Default
                  </button>
                  {!dim.restriction && !allChecked && <button type="button" data-testid={`filter-group-all-${dim.id}`}
                    onClick={() => !dim.options.length && onResetDefaults ? onResetDefaults(dim.id) : onChange({ ...selections, [dim.id]: allOptionValues(dim) })}
                    style={{ background: "none", border: 0, padding: 0, fontSize: T.fsXs, color: "var(--v2-accent)", cursor: "pointer" }}>
                    All
                  </button>}
                </span>;
              })() : <button
                type="button"
                data-testid={`filter-group-all-${dim.id}`}
                onClick={() => toggleGroupAll(dim.id)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: T.fsSm,
                  color: "var(--v2-accent)",
                  fontWeight: T.fwSemibold,
                  padding: "0 0 0 8px",
                }}
              >
                {dim.defaultValues ? "Default" : "All"}
              </button>}
            </div>
            {dim.options.map((opt) => {
              const checked = (selections[dim.id] ?? baselineSelections[dim.id] ?? []).includes(opt.value);
              return (
                <label
                  key={opt.value}
                  data-testid={`filter-option-${dim.id}-${opt.value}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "7px 16px",
                    cursor: "pointer",
                    fontSize: T.fsBase,
                    fontWeight: checked ? T.fwBold : T.fwRegular,
                    color: checked ? "var(--v2-dark)" : "var(--v2-mid)",
                    background: checked ? "var(--v2-tint)" : "transparent",
                    transition: "background .1s",
                  }}
                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = "var(--v2-hair)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = checked ? "var(--v2-tint)" : "transparent"; }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOption(dim.id, opt.value)}
                    style={{ accentColor: "var(--v2-accent)", width: 14, height: 14 }}
                  />
                  {opt.label}
                  {opt.count != null && <span style={{ marginLeft: "auto", color: T.mid, fontSize: T.fsXs }}>{opt.count}</span>}
                </label>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "10px 16px",
          borderTop: "1px solid var(--v2-hair)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 8,
        }}
      >
        {unifiedDefaults || totalGroupsActive > 0 ? (
          <button
            type="button"
            data-testid="filter-clear-all"
            onClick={clearAll}
            disabled={unifiedDefaults && totalGroupsActive === 0}
            style={{
              border: "none",
              background: "none",
              color: unifiedDefaults && totalGroupsActive === 0 ? "var(--v2-light)" : "var(--v2-accent)",
              fontSize: T.fsSm5,
              fontWeight: T.fwBold,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {unifiedDefaults ? "Reset all to defaults" : "↺ Reset to default"}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          data-testid="filter-done-btn"
          onClick={() => setOpen(false)}
          style={{
            border: "1px solid var(--v2-line)",
            background: "var(--v2-card)",
            borderRadius: 7,
            color: "var(--v2-dark)",
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            cursor: "pointer",
            padding: "5px 14px",
          }}
        >
          Done
        </button>
      </div>
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        data-testid={testId ?? "consolidated-filter-btn"}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          border: `1px solid ${showBadge ? "var(--v2-blue-border)" : "var(--v2-line)"}`,
          background: showBadge ? "var(--v2-blue-tint)" : "var(--v2-card)",
          borderRadius: 9,
          padding: "8px 13px",
          fontSize: T.fsSm5,
          fontWeight: showBadge ? T.fwBold : T.fwSemibold,
          color: showBadge ? "var(--v2-accent)" : "var(--v2-mid)",
          cursor: "pointer",
          transition: "all .15s",
        }}
      >
        <FilterIcon active={showBadge} />
        Filter
        {showBadge && (
          <span
            data-testid="filter-count-badge"
            style={{
              background: "var(--v2-accent)",
              color: T.white,
              borderRadius: 999,
              fontSize: T.fsXxs,
              fontWeight: T.fwBold,
              padding: "1px 6px",
              minWidth: 18,
              textAlign: "center",
            }}
          >
            {totalGroupsActive}
          </span>
        )}
      </button>

      {panel}
    </div>
  );
}

function FilterIcon({ active }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke={active ? "var(--v2-accent)" : "var(--v2-mid)"}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
